import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { pageIntros, pricingPlans } from '../data/site';
import SectionHeader from './SectionHeader';

export default function PricingCards({ showHeader = true }: { showHeader?: boolean }) {
  return (
    <section className="py-24" id="pricing">
      <div className="container-page">
        {showHeader && (
          /* Read from site.ts rather than repeated here: the two copies had
             already drifted apart once, and pricing copy is the worst place
             for a second source of truth. */
          <SectionHeader
            eyebrow={pageIntros.pricing.eyebrow}
            title={pageIntros.pricing.title}
            description={pageIntros.pricing.description}
          />
        )}
        {/* Four tiers: two up on tablet, four across on desktop. The old
            max-w-4xl two-column grid squeezed them to unreadable widths. */}
        <div className="mx-auto mt-14 grid max-w-6xl gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {pricingPlans.map((plan, index) => (
            <motion.article
              key={plan.name}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ delay: index * 0.06, duration: 0.5 }}
              className={`rounded-2xl border p-7 ${
                plan.featured
                  ? 'border-teal-400 bg-slate-950 text-white shadow-glow dark:bg-white dark:text-slate-950'
                  : 'border-slate-200 bg-white text-slate-950 shadow-premium dark:border-white/10 dark:bg-white/[0.06] dark:text-white'
              }`}
            >
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-xl font-semibold">{plan.name}</h3>
                {plan.badge && (
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      plan.featured
                        ? 'bg-teal-400/20 text-teal-200 dark:text-teal-700'
                        : 'bg-teal-50 text-teal-700 dark:bg-teal-400/10 dark:text-teal-300'
                    }`}
                  >
                    {plan.badge}
                  </span>
                )}
              </div>
              <div className="mt-8 flex items-end gap-2">
                <span className="text-5xl font-bold tracking-tight">{plan.price}</span>
                {plan.cadence && (
                  <span className="pb-2 text-sm text-slate-500 dark:text-slate-400">
                    {plan.cadence}
                  </span>
                )}
              </div>
              {plan.billingNote && (
                <p
                  className={`mt-3 text-sm font-semibold ${
                    plan.featured
                      ? 'text-teal-200 dark:text-teal-700'
                      : 'text-teal-700 dark:text-teal-300'
                  }`}
                >
                  {plan.billingNote}
                </p>
              )}
              <p
                className={`mt-5 leading-7 ${
                  plan.featured
                    ? 'text-slate-300 dark:text-slate-600'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                {plan.description}
              </p>
              <ul className="mt-7 grid gap-3">
                {plan.features.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm">
                    <Check size={17} className="text-teal-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
